import * as THREE from 'three';

export class CastleEnvironment {
  private group: THREE.Group;
  private torchLights: { light: THREE.PointLight; baseIntensity: number; phase: number }[] = [];
  private particlesMesh: THREE.Points | null = null;
  private particlePositions: Float32Array | null = null;
  private particleVelocities: Float32Array | null = null;

  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'CastleEnvironmentGroup';

    this.createCastleDais();
    this.createPillars();
    this.createArchesAndWalls();
    this.createTorches();
    this.createFloatingParticles();
  }

  public getGroup(): THREE.Group {
    return this.group;
  }

  private createCastleDais(): void {
    // Multi-tiered stone platform upon which the chessboard sits
    const daisMat = new THREE.MeshStandardMaterial({
      color: 0x1a1d24,
      roughness: 0.7,
      metalness: 0.2
    });

    // Tier 1: Large platform
    const t1 = new THREE.Mesh(new THREE.BoxGeometry(26, 0.5, 26), daisMat);
    t1.position.set(0, -0.4, 0);
    t1.receiveShadow = true;
    this.group.add(t1);

    // Tier 2: Foundation floor extending wide into the hall
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x101217,
      roughness: 0.85,
      metalness: 0.15
    });
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(120, 120), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, -0.65, 0);
    floor.receiveShadow = true;
    this.group.add(floor);
  }

  private createPillars(): void {
    // 8 Grand Gothic Stone Pillars framing the hall
    const pillarMat = new THREE.MeshStandardMaterial({
      color: 0x1f232b,
      roughness: 0.65,
      metalness: 0.25
    });

    const trimMat = new THREE.MeshStandardMaterial({
      color: 0xc8963e,
      roughness: 0.35,
      metalness: 0.75
    });

    const pillarPositions = [
      { x: -16, z: -16 },
      { x: 16, z: -16 },
      { x: -16, z: 0 },
      { x: 16, z: 0 },
      { x: -16, z: 16 },
      { x: 16, z: 16 },
      { x: -24, z: -28 },
      { x: 24, z: -28 }
    ];

    pillarPositions.forEach(({ x, z }) => {
      const pillarGroup = new THREE.Group();
      pillarGroup.position.set(x, 0, z);

      // Base plinth
      const base = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1.2, 2.4), pillarMat);
      base.position.y = 0;
      base.receiveShadow = true;
      base.castShadow = true;
      pillarGroup.add(base);

      // Octagonal column shaft
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.95, 18, 8), pillarMat);
      shaft.position.y = 9;
      shaft.castShadow = true;
      shaft.receiveShadow = true;
      pillarGroup.add(shaft);

      // Capital top
      const capital = new THREE.Mesh(new THREE.BoxGeometry(2.6, 1.4, 2.6), pillarMat);
      capital.position.y = 18;
      capital.castShadow = true;
      pillarGroup.add(capital);

      // Golden carved band
      const band = new THREE.Mesh(new THREE.CylinderGeometry(0.96, 0.96, 0.3, 16), trimMat);
      band.position.y = 6;
      pillarGroup.add(band);

      this.group.add(pillarGroup);
    });
  }

  private createArchesAndWalls(): void {
    // Distant gothic stone arches in the mist
    const archMat = new THREE.MeshStandardMaterial({
      color: 0x14161c,
      roughness: 0.8,
      metalness: 0.1
    });

    // North wall / background
    const backWall = new THREE.Mesh(new THREE.BoxGeometry(80, 28, 2), archMat);
    backWall.position.set(0, 12, -34);
    backWall.receiveShadow = true;
    this.group.add(backWall);

    // Left wall
    const leftWall = new THREE.Mesh(new THREE.BoxGeometry(2, 28, 80), archMat);
    leftWall.position.set(-34, 12, 0);
    leftWall.receiveShadow = true;
    this.group.add(leftWall);

    // Right wall
    const rightWall = new THREE.Mesh(new THREE.BoxGeometry(2, 28, 80), archMat);
    rightWall.position.set(34, 12, 0);
    rightWall.receiveShadow = true;
    this.group.add(rightWall);
  }

  private createTorches(): void {
    // 4 Wall Sconces with flickering magical fire
    const torchConfigs = [
      { x: -16, y: 5.5, z: -14 },
      { x: 16, y: 5.5, z: -14 },
      { x: -16, y: 5.5, z: 14 },
      { x: 16, y: 5.5, z: 14 }
    ];

    const bronzeMat = new THREE.MeshStandardMaterial({
      color: 0x5a4a30,
      roughness: 0.4,
      metalness: 0.8
    });

    const fireMat = new THREE.MeshBasicMaterial({
      color: 0xff7711
    });

    torchConfigs.forEach((cfg, idx) => {
      // Bracket
      const bracket = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 1.2, 8), bronzeMat);
      bracket.rotation.z = Math.PI / 6 * (cfg.x > 0 ? -1 : 1);
      bracket.position.set(cfg.x, cfg.y, cfg.z);
      this.group.add(bracket);

      // Flame bowl
      const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.2, 0.4, 12), bronzeMat);
      bowl.position.set(cfg.x + (cfg.x > 0 ? -0.4 : 0.4), cfg.y + 0.5, cfg.z);
      this.group.add(bowl);

      // Flame core
      const flame = new THREE.Mesh(new THREE.ConeGeometry(0.22, 0.65, 8), fireMat);
      flame.position.set(bowl.position.x, bowl.position.y + 0.4, cfg.z);
      this.group.add(flame);

      // Point Light with warm glow
      const light = new THREE.PointLight(0xff8833, 2.5, 28, 1.2);
      light.position.set(flame.position.x, flame.position.y + 0.2, cfg.z);
      light.castShadow = false; // keep perf snappy
      this.group.add(light);

      this.torchLights.push({
        light,
        baseIntensity: 2.2,
        phase: idx * 1.5
      });
    });
  }

  private createFloatingParticles(): void {
    // 250 drifting magical motes in the air
    const count = 250;
    const geom = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    const velocities = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      positions[i * 3 + 0] = (Math.random() - 0.5) * 40;
      positions[i * 3 + 1] = 0.5 + Math.random() * 14;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 40;

      velocities[i * 3 + 0] = (Math.random() - 0.5) * 0.3;
      velocities[i * 3 + 1] = 0.1 + Math.random() * 0.25;
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.3;
    }

    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const mat = new THREE.PointsMaterial({
      color: 0x66ddff,
      size: 0.16,
      transparent: true,
      opacity: 0.65,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });

    this.particlesMesh = new THREE.Points(geom, mat);
    this.particlePositions = positions;
    this.particleVelocities = velocities;
    this.group.add(this.particlesMesh);
  }

  public update(delta: number, time: number): void {
    // Torch flickering
    this.torchLights.forEach((t) => {
      const flicker = Math.sin(time * 12 + t.phase) * 0.3 + Math.cos(time * 23 + t.phase) * 0.15;
      t.light.intensity = t.baseIntensity + flicker;
    });

    // Mote drifting
    if (this.particlesMesh && this.particlePositions && this.particleVelocities) {
      const count = this.particlePositions.length / 3;
      for (let i = 0; i < count; i++) {
        this.particlePositions[i * 3 + 0] += this.particleVelocities[i * 3 + 0] * delta;
        this.particlePositions[i * 3 + 1] += this.particleVelocities[i * 3 + 1] * delta;
        this.particlePositions[i * 3 + 2] += this.particleVelocities[i * 3 + 2] * delta;

        // Reset if float too high
        if (this.particlePositions[i * 3 + 1] > 15) {
          this.particlePositions[i * 3 + 1] = 0.5;
          this.particlePositions[i * 3 + 0] = (Math.random() - 0.5) * 36;
          this.particlePositions[i * 3 + 2] = (Math.random() - 0.5) * 36;
        }
      }
      this.particlesMesh.geometry.attributes.position.needsUpdate = true;
    }
  }

  public setQuality(quality: 'high' | 'medium' | 'low'): void {
    if (this.particlesMesh) {
      this.particlesMesh.visible = quality !== 'low';
    }
  }
}
